import { plotGeometry, yFor } from '@/lib/status/plot';
import type { LatencyPoint } from '@/lib/status/model';

type Props = {
  series: LatencyPoint[];
  rows: number;
  /** How many samples the full width represents (the last-hour window). */
  window: number;
  p50: number | null;
  p99: number | null;
};

/** Minimum vertical distance between the two reference labels, in px. */
const LABEL_CLEARANCE = 15;

/**
 * The plot is a window of grid paper inside the white latency sheet: the box
 * is a whole number of cells tall and wide with its edges on page grid lines,
 * and its own hairlines (painted by the `[data-plot]` CSS) continue the body
 * grid through the cleared sheet at the same pitch. The series drawn is the
 * RAW ping per check — the real measurements, spikes intact — with p50 and
 * p99 as dashed reference levels.
 *
 * Nothing is labeled ON a line: y-axis labels sit in the gutter left of the
 * box, the time labels in the row under it, and the reference labels in
 * the gutter to its right, so text never collides with the geometry it
 * describes. The newest-sample dot is an HTML element (an SVG circle would be
 * stretched into an ellipse by the non-uniform viewBox scaling).
 */
export default function LatencyPlot({ series, rows, window: sampleWindow, p50, p99 }: Props) {
  const g = plotGeometry(series, { rows, window: sampleWindow });
  const empty = series.length === 0;
  // Row lines from the top down to the one above the baseline; "0 ms" is
  // implied by the baseline itself.
  const yLines = Array.from({ length: rows }, (_, i) => ({ i, ms: g.yMaxMs - i * g.rowMs }));
  const pct = (ms: number) => (yFor(ms, g.rowMs, rows) / rows) * 100;

  type Ref = { key: 'p50' | 'p99'; ms: number; stroke: string; text: string };
  const candidates: { key: 'p50' | 'p99'; ms: number | null; stroke: string; text: string }[] = [
    { key: 'p50', ms: p50, stroke: 'var(--color-brand-600)', text: 'text-brand-600' },
    { key: 'p99', ms: p99, stroke: 'var(--color-dim)', text: 'text-muted' },
  ];
  const refs: Ref[] = candidates.flatMap((r) =>
    typeof r.ms === 'number' && r.ms <= g.yMaxMs ? [{ ...r, ms: r.ms }] : [],
  );

  // Keep the two reference labels apart when the levels nearly coincide: the
  // higher one (p99) is pushed up by whatever clearance is missing.
  const labelTop: Record<string, string> = {};
  const p50Ref = refs.find((r) => r.key === 'p50');
  const p99Ref = refs.find((r) => r.key === 'p99');
  if (p50Ref) labelTop.p50 = `${pct(p50Ref.ms)}%`;
  if (p99Ref) {
    labelTop.p99 = p50Ref
      ? `calc(min(${pct(p99Ref.ms)}%, ${pct(p50Ref.ms)}% - ${LABEL_CLEARANCE}px))`
      : `${pct(p99Ref.ms)}%`;
  }

  return (
    <div>
      {/* The gutters are positioned against the plot box alone, not the time
          row beneath it, so a label's percentage top is the box's own scale. */}
      <div className="relative">
      {/* y-axis labels in the left gutter, centered on each row line. */}
      <div aria-hidden className="pointer-events-none absolute inset-y-0 right-full mr-2 hidden w-[calc(var(--grid-cell,72px)/2)] md:block">
        {yLines.map(({ i, ms }) => (
          <span
            key={ms}
            className={`absolute right-0 whitespace-nowrap font-jetbrains text-[10.5px] leading-none text-dim ${i === 0 ? 'pt-[3px]' : '-translate-y-1/2'}`}
            style={{ top: `${(i / rows) * 100}%` }}
          >
            {ms} ms
          </span>
        ))}
      </div>

      <div data-plot className="relative w-full" style={{ ['--rows' as string]: rows, aspectRatio: `3 / ${rows}` }}>
        <svg viewBox={`0 0 1 ${rows}`} preserveAspectRatio="none" className="absolute inset-0 block h-full w-full" aria-hidden="true">
          {refs.map((r) => {
            const y = yFor(r.ms, g.rowMs, rows);
            return (
              <line key={r.key} x1={0} x2={1} y1={y} y2={y} stroke={r.stroke} strokeWidth={1} strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
            );
          })}
          {g.rawPath && (
            <path d={g.rawPath} fill="none" stroke="var(--color-brand-600)" strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
          )}
        </svg>
        {g.last && (
          <span
            aria-hidden
            className="absolute h-[9px] w-[9px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-brand-600 shadow-[0_0_0_1px_var(--color-brand-600)]"
            style={{ left: `${g.last.x * 100}%`, top: `${(g.last.y / rows) * 100}%` }}
          />
        )}
        {empty && (
          <p className="absolute inset-0 grid place-items-center px-6 text-center text-[13.5px] text-muted">
            No latency samples yet — the routing probe hasn&rsquo;t reported in the last hour.
          </p>
        )}
      </div>

      {/* Reference labels in the right gutter, level with their lines. */}
      <div aria-hidden className="pointer-events-none absolute inset-y-0 left-full ml-2 hidden md:block">
        {refs.map((r) => (
          <span
            key={r.key}
            className={`absolute left-0 -translate-y-1/2 whitespace-nowrap font-jetbrains text-[10.5px] leading-none ${r.text}`}
            style={{ top: labelTop[r.key] }}
          >
            {r.key} {Math.round(r.ms)} ms
          </span>
        ))}
      </div>

      </div>

      {/* Time labels, vertically centered in the row under the box. */}
      <div className="flex items-center justify-between font-jetbrains text-[10.5px] leading-none text-dim max-md:pt-2 md:h-[var(--grid-cell,72px)]">
        <span>60 min ago</span>
        <span>now</span>
      </div>
    </div>
  );
}
